import { useEffect, useState } from 'react';
import { Trash2, ArchiveRestore, FlaskConical, Users, CalendarDays, Repeat, ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { companyApi } from '../../../lib/api';
import { toast } from 'sonner';

const ENTITY_META = {
  clients: { label: 'Client', Icon: Users, color: 'text-blue-600' },
  punctual_bookings: { label: 'Résa ponctuelle', Icon: CalendarDays, color: 'text-emerald-600' },
  subscription_bookings: { label: 'Abonnement', Icon: Repeat, color: 'text-purple-600' },
  appointment_bookings: { label: 'RDV', Icon: ClipboardList, color: 'text-amber-600' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function TrashPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await companyApi.getTrash();
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Impossible de charger la corbeille');
    } finally { setIsLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const restore = async (row) => {
    setBusyId(row.id);
    try {
      await companyApi.restoreEntity(row.entity, row.id);
      toast.success('Élément restauré');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de restauration');
    } finally { setBusyId(null); }
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.entity === filter);

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-2">
            <Trash2 className="w-7 h-7 text-primary" />
            Corbeille
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Réservations, clients et abonnements mis de côté. Restaurables à tout moment — rien n'est supprimé définitivement automatiquement.
          </p>
        </div>
        <Badge variant="outline" className="text-sm">Total : {total}</Badge>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { v: 'all', l: 'Tous' },
          { v: 'clients', l: 'Clients' },
          { v: 'subscription_bookings', l: 'Abonnements' },
          { v: 'punctual_bookings', l: 'Résas ponctuelles' },
          { v: 'appointment_bookings', l: 'RDV' },
        ].map(f => (
          <Button key={f.v} variant={filter === f.v ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f.v)} data-testid={`trash-filter-${f.v}`}>
            {f.l}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Éléments en corbeille</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">La corbeille est vide.</p>
          ) : (
            <div className="space-y-2">
              {filtered.map(row => {
                const meta = ENTITY_META[row.entity] || { label: row.entity, Icon: Trash2, color: 'text-muted-foreground' };
                const Icon = meta.Icon;
                return (
                  <div
                    key={`${row.entity}-${row.id}`}
                    className="flex items-center justify-between gap-3 p-3 border rounded-lg hover:bg-muted/30 transition-colors"
                    data-testid={`trash-row-${row.id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{row.label}</span>
                          <Badge variant="outline" className="text-xs">{meta.label}</Badge>
                          {row.is_test && (
                            <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 border-orange-300 text-xs gap-1">
                              <FlaskConical className="w-3 h-3" />
                              TEST
                            </Badge>
                          )}
                        </div>
                        {row.sub_label && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{row.sub_label}</p>
                        )}
                        <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                          Supprimé le {formatDate(row.deleted_at)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full flex-shrink-0"
                      onClick={() => restore(row)}
                      disabled={busyId === row.id}
                      data-testid={`restore-${row.id}`}
                    >
                      {busyId === row.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArchiveRestore className="w-4 h-4 mr-1" />}
                      Restaurer
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
